/* ============================================
   M-BUR CANDLE CO - COMPLETE ECOMMERCE TRACKER
   70+ Events Tracked - BATCHED VERSION
   ============================================ */

/* Client-side bot signals, stamped onto EVERY event so the Apps Script webhook
   can route likely-bot traffic to the "Bot Traffic" sheet instead of polluting
   real analytics. Declared at file top level (outside the tracker IIFE) so it is
   not nested inside another function and stays reachable from every event
   builder. Vanilla ES5 — var only, no optional chaining. */
function mburBotFlags() {
  var w = window.screen ? window.screen.width : 0;
  return {
    screen_width: w,                                   // server applies a screen_width===1366 bot rule
    is_headless: navigator.webdriver === true,         // Selenium/Puppeteer/Playwright
    ua_bot: /bot|crawler|spider|headless|phantom/i.test(navigator.userAgent),
    no_plugins: (navigator.plugins ? navigator.plugins.length : 0) === 0
  };
}

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  const CONFIG = {
    // Event pipeline moved to Supabase (edge function) — matches LIVE as of
    // 2026-08-11. The old Apps Script webhook now serves only the cached
    // Google-reviews endpoint. Do not revert this URL when syncing.
    WEBHOOK_URL: 'https://eaqnqtnomvbaeepqkrvz.supabase.co/functions/v1/track?k=XqduXA-pOjC4MnhQAB0ZeifbsfdCgxZO',
    DEBUG: false,
    STORAGE_PREFIX: 'mbur_',
    BATCH_INTERVAL: 5000,   // Send batch every 5 seconds
    BATCH_MAX_SIZE: 20,     // Or when 20 events accumulate
    // REALTIME: send every event immediately, one beacon each, in the order it
    // happens (no batching) — so rows arrive at the sheet in near-chronological
    // order. Trade-off: many more network requests (uses more Apps Script quota
    // and a bit more battery/data on mobile). It does NOT block the page, since
    // every send uses sendBeacon. Flip to false to go back to efficient batching.
    // NOTE: realtime does NOT guarantee in-order rows — concurrent beacons still
    // arrive/append out of order. Sort the sheet by event_seq/event_ms instead.
    REALTIME: false
  };

  // ============================================
  // EVENT QUEUE & BATCHING
  // ============================================
  let eventQueue = [];
  let batchTimer = null;

  function flushQueue() {
    if (eventQueue.length === 0) return;

    // init() no longer waits on the IP lookup (it used to stall all tracking up
    // to 2s behind api.ipify.org). Instead, the first flush waits here until the
    // IP race settles, then re-checks the blocklist — so blocked/internal IPs
    // still never reach the sheet, but page tracking starts immediately.
    if (!_ipReady) {
      if (!_ipFlushWaiting) {
        _ipFlushWaiting = true;
        (_ipPromise || Promise.resolve()).then(function() {
          _ipFlushWaiting = false;
          flushQueue();
        });
      }
      return;
    }
    if (isBlockedIP()) { eventQueue.length = 0; return; }

    const batch = eventQueue.splice(0);
    const payload = JSON.stringify({ events: batch });

    if (CONFIG.DEBUG) console.log('[MburTracker] Flushing', batch.length, 'events');

    if (navigator.sendBeacon) {
      // sendBeacon returns false when the browser's beacon queue is full (a
      // shared ~64KB limit). If that happens, retry via a keepalive fetch so the
      // batch isn't silently dropped — dropped beacons are what caused the
      // missing event_seq numbers in the sheet.
      var queued = navigator.sendBeacon(CONFIG.WEBHOOK_URL, payload);
      if (!queued) {
        fetch(CONFIG.WEBHOOK_URL, {
          method: 'POST',
          body: payload,
          keepalive: true
        }).catch(function () {});
      }
    } else {
      fetch(CONFIG.WEBHOOK_URL, {
        method: 'POST',
        body: payload,
        keepalive: true
      }).catch(() => {});
    }
  }

  function startBatchTimer() {
    if (batchTimer) return;
    batchTimer = setInterval(function() {
      flushQueue();
    }, CONFIG.BATCH_INTERVAL);
  }

  function queueEvent(payload) {
    eventQueue.push(payload);

    // REALTIME mode flushes after every single event so rows arrive in order;
    // otherwise wait until the batch fills up.
    if (CONFIG.REALTIME || eventQueue.length >= CONFIG.BATCH_MAX_SIZE) {
      flushQueue();
    }
  }

  // Flush on page unload
  var sessionEnded = false;

  window.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'hidden') {
      flushQueue();
    }
  });

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  function log(msg, data) {
    if (CONFIG.DEBUG) console.log('[MburTracker]', msg, data || '');
  }

  function generateId() {
    return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

  // These run at script load. Private / locked-down browsers can THROW on
  // storage access — without a guard that kills the entire tracker and the
  // visitor produces zero events. Always degrade instead of throwing.
  function getVisitorId() {
    try {
      var id = localStorage.getItem(CONFIG.STORAGE_PREFIX + 'visitor_id');
      if (!id) {
        id = 'v_' + generateId();
        localStorage.setItem(CONFIG.STORAGE_PREFIX + 'visitor_id', id);
      }
      return id;
    } catch (e) {
      return 'v_' + generateId(); // ephemeral — still lets the session be tracked
    }
  }

  function getSessionId() {
    try {
      var id = sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'session_id');
      if (!id) {
        id = 's_' + generateId();
        sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'session_id', id);
      }
      return id;
    } catch (e) {
      return 's_' + generateId();
    }
  }

  function isReturningVisitor() {
    try {
      var isReturn = sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'is_return');
      if (isReturn === null) {
        var lastVisit = localStorage.getItem(CONFIG.STORAGE_PREFIX + 'last_visit');
        isReturn = !!lastVisit ? 'true' : 'false';
        sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'is_return', isReturn);
        localStorage.setItem(CONFIG.STORAGE_PREFIX + 'last_visit', Date.now().toString());
      }
      return isReturn === 'true';
    } catch (e) {
      return false;
    }
  }

  function getUTMParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || '',
      gclid: params.get('gclid') || '',
      fbclid: params.get('fbclid') || '',
      mc_eid: params.get('mc_eid') || '',
      mc_cid: params.get('mc_cid') || ''
    };
  }

  function getPageType() {
    const path = window.location.pathname;
    if (path === '/') return 'home';
    if (path.includes('/products/')) return 'product';
    if (path.includes('/collections/')) return 'collection';
    if (path.includes('/blogs/')) return 'blog';
    if (path.includes('/cart')) return 'cart';
    if (path.includes('/checkout')) return 'checkout';
    if (path.includes('/pages/')) return 'page';
    if (path.includes('/search')) return 'search';
    if (path.includes('/account')) return 'account';
    return 'other';
  }

  // ShopifyAnalytics.meta.product has NO `title` key. Verified live 2026-08-19:
  // its keys are id, gid, vendor, type, handle, variants, remote. The old code
  // read p.title, got undefined, and sent product_name:'' — which the pipeline
  // drops — so product_name landed NULL on 99.9% of product_view rows while
  // product_id (p.id, which does exist) populated fine. Derive the name from
  // fields that actually exist.
  function nameFromShopifyMeta(p) {
    if (!p) return '';
    if (p.title) return String(p.title).trim();            // future-proof
    const v = p.variants && p.variants[0];
    if (v && v.name) {
      // v.name is "Product Title - Variant Title"; strip the variant suffix
      // using public_title so multi-word product names survive intact.
      if (v.public_title && v.name.indexOf(' - ' + v.public_title) > -1) {
        return v.name.replace(' - ' + v.public_title, '').trim();
      }
      return String(v.name).trim();
    }
    return '';
  }

  // Product title as shown on a product page. og:title is the product title
  // exactly; document.title is NOT (it carries SEO suffixes), so it is only
  // used as a last resort and never on non-product pages.
  function nameFromProductPage() {
    const og = document.querySelector('meta[property="og:title"]');
    if (og && og.content) return og.content.trim();
    const h = document.querySelector('.pp-buybox__title, .product-info__title, .mbur-product__title, h1');
    if (h && h.textContent.trim()) return h.textContent.trim();
    return '';
  }

  function humanizeHandle(handle) {
    if (!handle) return '';
    return String(handle).split('-').map(function (w) {
      return w ? w.charAt(0).toUpperCase() + w.slice(1) : w;
    }).join(' ').trim();
  }

  // Best-effort product name from a product CARD (collection grids, carousels,
  // cart-drawer recommendations, search results, inline blog cards).
  const CARD_TITLE_SEL =
    '.cp-card__title, .product-card__title, .related-card__title, .product-title, ' +
    '.cp-cross__name, .cart-drawer__rec-title, .search-product-card__title, ' +
    '.hp-product-card__title, [data-product-title], h3, h2';
  const CARD_ROOT_SEL = '.product-card, .cp-card, .related-card, [data-product-card]';

  function nameFromCardElement(el) {
    if (!el || !el.querySelector) return '';
    const t = el.querySelector(CARD_TITLE_SEL);
    if (t && t.textContent.trim()) return t.textContent.trim().substring(0, 120);
    const link = el.querySelector('a[href*="/products/"]') ||
                 (el.closest ? el.closest('a[href*="/products/"]') : null);
    if (link) {
      const aria = link.getAttribute('aria-label');
      if (aria && aria.trim()) return aria.trim().substring(0, 120);
    }
    const img = el.querySelector('img[alt]');
    if (img && img.getAttribute('alt') && img.getAttribute('alt').trim()) {
      return img.getAttribute('alt').trim().substring(0, 120);
    }
    if (link) {
      const href = link.getAttribute('href') || '';
      const h = (href.split('/products/')[1] || '').split('?')[0].split('/')[0];
      if (h) return humanizeHandle(h);
    }
    return '';
  }

  function nameFromCard(card) {
    if (!card) return '';
    const direct = nameFromCardElement(card);
    if (direct) return direct;
    // The observed element may be a CONTROL inside a card rather than the card
    // itself — the wishlist button carries data-product-id and so matches the
    // impression selector, but holds no title, link or image. Climb to the
    // enclosing card for the name. (Only the name is taken from the ancestor;
    // the handle/URL used for impression de-duplication is left untouched so
    // event counts are unchanged.)
    const owner = card.closest ? card.closest(CARD_ROOT_SEL) : null;
    if (owner && owner !== card) return nameFromCardElement(owner);
    return '';
  }

  function getProductInfo() {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const p = window.ShopifyAnalytics.meta.product;
      let name = nameFromShopifyMeta(p);
      if (!name && getPageType() === 'product') name = nameFromProductPage();
      if (!name) name = humanizeHandle(p.handle);
      return {
        product_name: name || '',
        product_id: p.id || '',
        product_price: p.variants && p.variants[0] ? p.variants[0].price : '',
        product_collection: p.type || '',
        product_vendor: p.vendor || ''
      };
    }
    // No Shopify meta. Only a PRODUCT page can tell us a product name here —
    // on a collection/blog page og:title is the collection/article title, and
    // reporting that as product_name was actively wrong data.
    const price = document.querySelector('meta[property="product:price:amount"]');
    return {
      product_name: getPageType() === 'product' ? nameFromProductPage() : '',
      product_price: price ? price.content : '',
      product_collection: ''
    };
  }

  function getDeviceType() {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) {
      return 'Mobile';
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      return 'Tablet';
    }
    return 'Desktop';
  }

  function getScreenInfo() {
    return {
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight
    };
  }

  function incrementPageCount() {
    try {
      var count = parseInt(sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'page_count') || '0');
      sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'page_count', (count + 1).toString());
      return count + 1;
    } catch (e) {
      return 1;
    }
  }

  // ============================================
  // BROWSER FINGERPRINT (matches across storefront + pixel)
  // ============================================

  function getFingerprint() {
    var parts = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.hardwareConcurrency || '',
      navigator.platform || ''
    ];
    var hash = 0;
    var str = parts.join('|');
    for (var i = 0; i < str.length; i++) {
      var char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return 'fp_' + Math.abs(hash).toString(36);
  }

  const _fingerprint = getFingerprint();

  // ============================================
  // BLOCK INTERNAL TRAFFIC
  // ============================================

  // IP addresses to exclude from tracking
  const BLOCKED_IPS = [
    '108.6.132.228',
    '101.44.163.236'   // Huawei Cloud datacenter IP — confirmed bot (multi-tab crawl)
  ];

  // Crawler / datacenter IP RANGES. Search bots rotate across hundreds of
  // addresses, so match the network prefix instead of listing each one. Needed
  // because Bing's JS renderer uses a normal Chrome user agent, so isBot()
  // can't catch it by UA alone.
  // NOTE: this only excludes them from ANALYTICS — it does not block them from
  // the site, so search indexing is completely unaffected.
  const BLOCKED_IP_PREFIXES = [
    '40.77.',    // Bingbot  (msnbot-*.search.msn.com)
    '157.55.',   // Bingbot
    '207.46.',   // Bingbot / MSN
    '13.66.',    // Microsoft Azure crawlers
    '66.249.',   // Googlebot
    '101.44.'    // Huawei Cloud scraper
  ];

  // Detect automated / headless browsers and known bot user agents.
  // Bots rotate IPs, so this signal-based check catches far more than the
  // IP blocklist above (which is only for specific repeat offenders).
  function isBot() {
    // Headless & automation frameworks expose navigator.webdriver === true
    if (navigator.webdriver === true) return true;

    var ua = (navigator.userAgent || '').toLowerCase();
    if (!ua) return true; // a browser with no user-agent string is itself bot-like

    var botSignatures = [
      'bot', 'crawl', 'spider', 'slurp', 'headless', 'phantom',
      'puppeteer', 'playwright', 'selenium', 'python', 'curl/', 'wget',
      'httpclient', 'java/', 'go-http', 'axios', 'okhttp', 'scrapy',
      'lighthouse', 'pagespeed', 'gtmetrix', 'facebookexternalhit',
      'embedly', 'whatsapp', 'telegrambot', 'discordbot', 'ahrefs',
      'semrush', 'mj12bot', 'dotbot', 'petalbot'
    ];
    for (var i = 0; i < botSignatures.length; i++) {
      if (ua.indexOf(botSignatures[i]) > -1) return true;
    }
    return false;
  }

  function isInternalTraffic() {
    // Automated / headless / crawler traffic — exclude from all analytics
    if (isBot()) {
      return true;
    }

    // Cookie-based exclusion: set cookie mbur_no_track=1 in your browser
    if (document.cookie.indexOf('mbur_no_track=1') > -1) {
      return true;
    }

    // Only the CURRENT PAGE's url can tell us we're in the admin/theme editor.
    // A bare 'shopify.com' match used to be checked here — that was wrong:
    // Shopify's CHECKOUT is hosted on a shopify.com domain, so real customers
    // returning from checkout (or arriving via Shop Pay / the Shop app) had
    // every event silently discarded as "internal traffic". Same for a loose
    // 'editor' match, which can appear inside ordinary blog/product URLs.
    var urlPatterns = [
      '/admin',
      'preview_theme_id',
      'design_mode'
    ];
    var url = window.location.href.toLowerCase();
    for (var i = 0; i < urlPatterns.length; i++) {
      if (url.indexOf(urlPatterns[i]) > -1) {
        return true;
      }
    }

    // The referrer only means "internal" when it's the Shopify ADMIN itself.
    var referrer = (document.referrer || '').toLowerCase();
    if (referrer.indexOf('myshopify.com/admin') > -1 ||
        referrer.indexOf('admin.shopify.com') > -1) {
      return true;
    }

    if (window.Shopify && window.Shopify.designMode) {
      return true;
    }

    return false;
  }

  // IP-based exclusion: checked after IP is fetched
  function isBlockedIP() {
    if (BLOCKED_IPS.indexOf(_clientIP) > -1) return true;
    for (var i = 0; i < BLOCKED_IP_PREFIXES.length; i++) {
      if (_clientIP.indexOf(BLOCKED_IP_PREFIXES[i]) === 0) return true;
    }
    return false;
  }

  // ============================================
  // SEND EVENT (NOW BATCHED)
  // ============================================

  // Cache values that don't change per page
  const _visitorId = getVisitorId();
  const _sessionId = getSessionId();
  const _utm = getUTMParams();
  const _screen = getScreenInfo();
  const _deviceType = getDeviceType();
  const _pageType = getPageType();
  const _isReturn = isReturningVisitor();

  // Get client IP address (cached)
  var _clientIP = 'unknown';
  var _ipReady = false;
  var _ipPromise = null;       // settled promise from fetchClientIP()
  var _ipFlushWaiting = false; // re-entrancy guard for the flush that waits on IP

  function fetchClientIP() {
    // boot() waits on this before init(), so it must ALWAYS settle. An ad
    // blocker that makes this request HANG (rather than fail) would otherwise
    // stall boot forever and the visitor would produce zero events. Race it
    // against a 2s timeout — an 'unknown' IP is far better than losing the
    // whole session.
    var lookup = fetch('https://api.ipify.org?format=json')
      .then(function(response) { return response.json(); })
      .then(function(data) {
        if (data && data.ip) {
          _clientIP = data.ip;
          log('Client IP detected:', _clientIP);
        }
      })
      .catch(function(err) {
        log('Failed to get IP:', err);
      });

    var timeout = new Promise(function(resolve) { setTimeout(resolve, 2000); });

    return Promise.race([lookup, timeout]).then(function() {
      _ipReady = true;
    });
  }

  function sendEvent(eventType, extraData = {}) {
    if (isInternalTraffic() || isBlockedIP()) {
      log('Blocked internal traffic');
      return;
    }

    const payload = {
      timestamp: new Date().toISOString(),
      event_seq: nextEventSeq(),
      event_ms: Date.now(),
      event_type: eventType,
      visitor_id: _visitorId,
      session_id: _sessionId,
      client_ip: _clientIP,
      fingerprint: _fingerprint,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      page_type: _pageType,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      device_type: _deviceType,
      screen_width: _screen.screen_width,
      screen_height: _screen.screen_height,
      viewport_width: _screen.viewport_width,
      viewport_height: _screen.viewport_height,
      utm_source: _utm.utm_source,
      utm_medium: _utm.utm_medium,
      utm_campaign: _utm.utm_campaign,
      utm_term: _utm.utm_term,
      utm_content: _utm.utm_content,
      gclid: _utm.gclid,
      fbclid: _utm.fbclid,
      mc_eid: _utm.mc_eid,
      mc_cid: _utm.mc_cid,
      return_visitor: _isReturn,
      ...extraData
    };

    // Bot flags applied AFTER extraData so they can never be shadowed by a
    // caller-supplied field. screen_width is already present above with the
    // same window.screen.width integer, so this overwrites like-for-like
    // rather than adding a duplicate key.
    Object.assign(payload, mburBotFlags());

    log('Queued:', payload);
    queueEvent(payload);
  }

  // Remember the most recent cart item count so session_end can tell whether
  // the visitor abandoned a non-empty cart (cart_abandon_intent).
  function rememberCartCount(count) {
    try {
      sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'last_cart_count', String(count));
    } catch (e) {}
  }

  // Monotonically increasing per-session counter stamped on every event.
  // The sheet stores rows in the order batches ARRIVE (not the order events
  // happened) and rounds the timestamp to the minute — so sorting by event_seq
  // (or the raw event_ms) reconstructs the true chronological order.
  var _seqFallback = 0;
  function nextEventSeq() {
    try {
      var n = parseInt(sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'event_seq') || '0', 10) + 1;
      sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'event_seq', String(n));
      return n;
    } catch (e) {
      return ++_seqFallback; // storage blocked — count in memory instead
    }
  }

  // ============================================
  // CRITICAL EVENTS (SEND IMMEDIATELY)
  // These bypass the queue for time-sensitive tracking
  // ============================================

  function sendCriticalEvent(eventType, extraData = {}) {
    // Same IP-gate as flushQueue: if the ipify race hasn't settled, defer this
    // send until it does (≤2s), so the blocklist check below sees the real IP.
    if (!_ipReady) {
      (_ipPromise || Promise.resolve()).then(function() {
        sendCriticalEvent(eventType, extraData);
      });
      return;
    }
    if (isInternalTraffic() || isBlockedIP()) return;

    const payload = {
      events: [{
        timestamp: new Date().toISOString(),
        event_seq: nextEventSeq(),
        event_ms: Date.now(),
        event_type: eventType,
        visitor_id: _visitorId,
        session_id: _sessionId,
        client_ip: _clientIP,
        fingerprint: _fingerprint,
        page_url: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        page_type: _pageType,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        device_type: _deviceType,
        screen_width: _screen.screen_width,
        screen_height: _screen.screen_height,
        viewport_width: _screen.viewport_width,
        viewport_height: _screen.viewport_height,
        utm_source: _utm.utm_source,
        utm_medium: _utm.utm_medium,
        utm_campaign: _utm.utm_campaign,
        utm_term: _utm.utm_term,
        utm_content: _utm.utm_content,
        gclid: _utm.gclid,
        fbclid: _utm.fbclid,
        mc_eid: _utm.mc_eid,
        mc_cid: _utm.mc_cid,
        return_visitor: _isReturn,
        ...extraData
      }]
    };

    // Stamp the EVENT OBJECT inside the events array — not the outer envelope.
    // The server reads these per-event; envelope-level fields are never seen.
    Object.assign(payload.events[0], mburBotFlags());

    var body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      // Retry via keepalive fetch if the beacon queue is full (see flushQueue).
      if (!navigator.sendBeacon(CONFIG.WEBHOOK_URL, body)) {
        fetch(CONFIG.WEBHOOK_URL, {
          method: 'POST',
          body: body,
          keepalive: true
        }).catch(function () {});
      }
    } else {
      fetch(CONFIG.WEBHOOK_URL, {
        method: 'POST',
        body: body,
        keepalive: true
      }).catch(() => {});
    }
  }

  // ============================================
  // CORE EVENTS
  // ============================================

  function trackSessionStart() {
    const sessionStarted = sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'session_started');
    if (!sessionStarted) {
      sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'session_started', 'true');
      sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'session_start_time', Date.now().toString());
      sendEvent('session_start', {
        landing_page: window.location.href
      });
    }
  }

  function trackPageView() {
    const pageCount = incrementPageCount();
    sendEvent('page_view', {
      page_number: pageCount
    });
  }

  function trackProductView() {
    if (getPageType() === 'product') {
      const info = getProductInfo();
      sendEvent('product_view', info);
    }
  }

  function trackCollectionView() {
    if (getPageType() === 'collection') {
      const path = window.location.pathname;
      const collection = path.split('/collections/')[1]?.split('/')[0] || '';
      const productCount = document.querySelectorAll('.product-card, .product-item, [data-product-card]').length;
      sendEvent('collection_view', {
        collection_name: collection,
        product_count: productCount
      });
    }
  }

  function trackBlogRead() {
    if (getPageType() === 'blog') {
      const articleTitle = document.querySelector('h1, .article-title, .blog-title');
      sendEvent('blog_read', {
        article_title: articleTitle ? articleTitle.textContent.trim() : document.title
      });
    }
  }

  function trackCartView() {
    if (getPageType() === 'cart') {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          rememberCartCount(cart.item_count);
          sendEvent('cart_view', {
            cart_value: (cart.total_price / 100).toFixed(2),
            cart_item_count: cart.item_count
          });
        })
        .catch(() => {
          sendEvent('cart_view');
        });
    }
  }

  function trackCheckoutInitiated() {
    if (getPageType() === 'checkout') {
      const checkoutTracked = sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'checkout_tracked');
      if (!checkoutTracked) {
        sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'checkout_tracked', 'true');
        sendCriticalEvent('checkout_initiated');
      }
    }
  }

  function trackPurchaseComplete() {
    var path = window.location.pathname;
    if (!path.includes('/thank_you') && !path.includes('/orders/')) return;

    var purchaseTracked = sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'purchase_tracked');
    if (purchaseTracked) return;
    sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'purchase_tracked', 'true');

    var data = {
      purchase_url: window.location.href
    };

    if (window.Shopify && window.Shopify.checkout) {
      var co = window.Shopify.checkout;
      data.order_id = co.order_id || '';
      data.order_total = co.total_price || '';
      data.order_subtotal = co.subtotal_price || '';
      data.order_tax = co.total_tax || '';
      data.order_shipping = co.shipping_rate && co.shipping_rate.price || '';
      data.order_currency = co.currency || '';
      data.order_discount = co.discount && co.discount.amount || '';
      data.order_discount_code = co.discount && co.discount.code || '';
      data.order_email = co.email || '';
      data.order_item_count = co.line_items ? co.line_items.length : 0;
      if (co.line_items) {
        data.order_items = co.line_items.map(function(item) {
          return item.title + ' x' + item.quantity;
        }).join(', ');
      }
    }

    sendCriticalEvent('purchase_complete', data);
  }

  function track404Page() {
    const is404 =
      window.location.pathname.includes('/404') ||
      document.body.classList.contains('template-404') ||
      document.querySelector('[data-template="404"]') ||
      (typeof Shopify !== 'undefined' && Shopify.designMode === false &&
       document.querySelector('.errors, .not-found, [class*="404"]'));

    if (is404) {
      sendEvent('404_page', {
        attempted_url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer || 'direct'
      });
    }
  }

  // ============================================
  // ADD TO CART + VISITOR ID TO CART
  // ============================================

  function setupAddToCartTracking() {
    function addVisitorToCart() {
      var visitorId = getVisitorId();
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { 'visitor_id': visitorId }
        })
      }).catch(function() {});
    }

    function getCartAndTrack(productInfo) {
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var cartItems = cart.items.map(function(item) {
            return item.title + ' x' + item.quantity;
          }).join(', ');

          rememberCartCount(cart.item_count);

          // Resolve the product from the cart LINE matching the variant we just
          // added. This is authoritative and stays correct with several items in
          // the cart — unlike parsing the cart_items string, which describes the
          // whole cart. Page/card context still wins when it already has a name
          // (product pages), this only fills the gaps (collection grids, quick
          // add, cart-drawer recommendations) where product_name was NULL.
          var addedLine = null;
          var vid = parseInt(productInfo.variant_id, 10);
          if (vid) {
            for (var li = 0; li < cart.items.length; li++) {
              if (cart.items[li].variant_id === vid) { addedLine = cart.items[li]; break; }
            }
          }

          // Add to cart is critical — send immediately
          sendCriticalEvent('add_to_cart', {
            product_name: productInfo.product_name || (addedLine ? addedLine.product_title : '') || '',
            product_id: productInfo.product_id || (addedLine ? addedLine.product_id : '') || '',
            product_price: productInfo.product_price || '',
            variant_id: productInfo.variant_id || '',
            variant_title: productInfo.variant_title || (addedLine ? addedLine.variant_title : '') || '',
            quantity_added: productInfo.quantity || 1,
            cart_value: (cart.total_price / 100).toFixed(2),
            cart_item_count: cart.item_count,
            cart_items: cartItems
          });

          addVisitorToCart();
        })
        .catch(function() {
          sendCriticalEvent('add_to_cart', productInfo);
          addVisitorToCart();
        });
    }

    // Track form submissions
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        var productInfo = getProductInfo();

        // Adds from a collection grid / carousel card: the page-level product
        // meta is absent (or belongs to a different product), so take the name
        // from the card the form sits in. getCartAndTrack still backstops this
        // from the cart line.
        if (!productInfo.product_name && form.closest) {
          var card = form.closest('.cp-card, .product-card, [data-product-card], .search-product-card, .cart-drawer__rec-item, .cp-cross__item');
          if (card) productInfo.product_name = nameFromCard(card);
        }

        var qtyInput = form.querySelector('input[name="quantity"]');
        productInfo.quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

        // Same trap as the cart handler: querySelector returns the FIRST field
        // named "id", which in a radio group (the 12-scent sample product) is
        // never the checked one. FormData resolves radios/select/hidden the way
        // the browser does, so analytics records the scent actually added.
        var variantId = null;
        try { variantId = new FormData(form).get('id'); } catch (e) {}
        if (variantId === null || variantId === '') {
          var variantInput = form.querySelector('input[name="id"], select[name="id"]');
          variantId = variantInput ? variantInput.value : null;
        }
        if (variantId) {
          productInfo.variant_id = variantId;
        }

        setTimeout(function() {
          getCartAndTrack(productInfo);
        }, 500);
      }
    });

    // Track fetch-based adds (AJAX)
    var origFetch = window.fetch;
    window.fetch = function(url, options) {
      // Fire the real request first so we can wait for IT to finish
      var _fetchResult = origFetch.apply(this, arguments);

      if (typeof url === 'string' && url.includes('/cart/add')) {
        var productInfo = getProductInfo();

        if (options && options.body) {
          try {
            var body = JSON.parse(options.body);
            productInfo.variant_id = body.id || body.items?.[0]?.id || '';
            productInfo.quantity = body.quantity || body.items?.[0]?.quantity || 1;
          } catch (e) {}
        }

        // Read the cart AFTER the add actually commits — not a fixed 500ms guess —
        // so cart_value / cart_item_count aren't captured as 0 on a slow first add.
        if (_fetchResult && typeof _fetchResult.then === 'function') {
          _fetchResult
            .then(function (res) {
              // A non-OK /cart/add response means the add FAILED (sold out,
              // variant unavailable, quantity limit). Capture it — otherwise a
              // lost sale looks identical to no attempt at all.
              if (res && res.ok === false) {
                var status = res.status;
                var fire = function (msg) {
                  sendCriticalEvent('add_to_cart_failed', {
                    product_name: productInfo.product_name || '',
                    product_id: productInfo.product_id || '',
                    variant_id: productInfo.variant_id || '',
                    quantity_attempted: productInfo.quantity || 1,
                    error_status: status,
                    error_message: msg || 'Add to cart failed'
                  });
                };
                res.clone().json()
                  .then(function (err) { fire(err && (err.description || err.message)); })
                  .catch(function () { fire('Add to cart failed'); });
                return; // don't record a successful add
              }
              getCartAndTrack(productInfo);
            })
            .catch(function () { getCartAndTrack(productInfo); });
        } else {
          setTimeout(function () { getCartAndTrack(productInfo); }, 500);
        }
      }

      // Track cart changes — batched, not critical
      if (typeof url === 'string' && url.includes('/cart/change')) {
        setTimeout(function() {
          origFetch.call(window, '/cart.js')
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              var cartItems = cart.items.map(function(item) {
                return item.title + ' x' + item.quantity;
              }).join(', ');

              rememberCartCount(cart.item_count);
              sendEvent('cart_updated', {
                cart_value: (cart.total_price / 100).toFixed(2),
                cart_item_count: cart.item_count,
                cart_items: cartItems
              });
            })
            .catch(function() {});
        }, 500);
      }

      return _fetchResult;
    };

    // Track checkout clicks
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
      if (btn) {
        addVisitorToCart();
      }
    });
  }

  function setupTextInputTracking() {
    document.addEventListener('blur', function(e) {
      var input = e.target;

      if (input.tagName !== 'INPUT' && input.tagName !== 'TEXTAREA') return;

      var skipTypes = ['password', 'hidden', 'submit', 'button', 'reset'];
      if (skipTypes.indexOf(input.type) > -1) return;

      if (!input.value || input.value.trim() === '') return;

      var fieldName = input.name || input.id || input.placeholder || input.type || 'unknown';
      var fieldValue = input.value.trim();

      if (input.type === 'search' || fieldName.toLowerCase().includes('search') || fieldName === 'q') {
        sendEvent('search_input', { search_term: fieldValue });
        return;
      }

      if (input.type === 'email' || fieldName.toLowerCase().includes('email')) {
        sendCriticalEvent('email_entered', {
          email: fieldValue,
          form_location: getFormLocation(input)
        });
        return;
      }

      /* ZIP/postal field in the shipping estimator — log it as its own event
         (not as a promo code, which its "...code" placeholder would otherwise trip) */
      var isZipField = fieldName.toLowerCase().includes('zip') ||
                       fieldName.toLowerCase().includes('postal') ||
                       input.classList.contains('js-shipping-zip') ||
                       input.getAttribute('aria-label') === 'ZIP or postal code';
      if (isZipField) {
        sendEvent('shipping_zip_entered', {
          field_value: fieldValue.substring(0, 20),
          form_location: getFormLocation(input)
        });
        return;
      }

      if (fieldName.toLowerCase().includes('promo') ||
          fieldName.toLowerCase().includes('discount') ||
          fieldName.toLowerCase().includes('coupon') ||
          fieldName.toLowerCase().includes('code')) {
        sendEvent('promo_code_entered', { promo_code: fieldValue });
        return;
      }

      var valuableFields = ['email', 'phone', 'promo', 'discount', 'coupon', 'code', 'search', 'q', 'query'];
      var isValuable = valuableFields.some(function(f) {
        return fieldName.toLowerCase().includes(f);
      });

      if (isValuable || input.tagName === 'TEXTAREA') {
        sendEvent('text_input', {
          field_name: fieldName,
          field_value: fieldValue.substring(0, 200),
          field_type: input.type || 'textarea',
          form_location: getFormLocation(input)
        });
      }
    }, true);

    function getFormLocation(input) {
      if (!input || !input.closest) return 'page';
      var form = input.closest('form');
      if (form) {
        return form.id || form.action || form.className.split(' ')[0] || 'unknown_form';
      }
      var section = input.closest('section, .popup, .modal, footer, header');
      if (section) {
        return section.id || section.className.split(' ')[0] || 'page';
      }
      return 'page';
    }
  }

  // ============================================
  // CART CONTENTS & UPDATES
  // ============================================

  function trackCartContents() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        var items = cart.items.map(function(item) {
          return item.title + ' (x' + item.quantity + ')';
        }).join(', ');

        sendEvent('cart_contents', {
          cart_items: items,
          cart_value: (cart.total_price / 100).toFixed(2),
          cart_item_count: cart.item_count
        });
      })
      .catch(() => {});
  }

  function setupCartRemoveTracking() {
    document.addEventListener('click', function(e) {
      var removeBtn = e.target.closest('[data-cart-remove], .cart-remove, .remove-item, a[href*="/cart/change"]');
      if (removeBtn) {
        sendEvent('cart_item_removed');
      }
    });
  }

  function setupOrderNotesTracking() {
    /* Track when customer finishes typing order notes in cart drawer or cart page */
    document.addEventListener('blur', function(e) {
      var noteInput = e.target.closest('.js-cart-note, #cart-note-field, textarea[name="note"]');
      if (noteInput && noteInput.value.trim()) {
        sendEvent('order_note_added', {
          order_note: noteInput.value.trim().substring(0, 300)
        });
      }
    }, true);
  }

  // ============================================
  // BUTTON CLICK TRACKING
  // Throttled: max 1 button_click event per 500ms
  // ============================================

  function setupButtonClickTracking() {
    var lastClickTime = 0;

    document.addEventListener('click', function(e) {
      var now = Date.now();
      if (now - lastClickTime < 500) return;
      lastClickTime = now;

      var target = e.target.closest('button, a, [role="button"], input[type="submit"], input[type="button"]');
      if (!target) return;

      if (target.closest('form[action*="/cart"]')) return;
      // Skip nav links — tracked by menu_click
      if (target.closest('nav, .nav, .menu, .header')) return;

      var buttonText = '';
      if (target.tagName === 'INPUT') {
        buttonText = target.value || target.placeholder || '';
      } else {
        buttonText = target.innerText || target.textContent || '';
      }

      buttonText = buttonText.trim().replace(/\s+/g, ' ').substring(0, 100);

      if (!buttonText || buttonText.length < 2) {
        buttonText = target.getAttribute('aria-label') ||
                     target.getAttribute('title') ||
                     target.className.split(' ')[0] ||
                     'icon_button';
      }

      var buttonLocation = 'page';
      if (target.closest('footer, .footer')) buttonLocation = 'footer';
      else if (target.closest('.cart-drawer, #cart-drawer, [data-cart-drawer]')) buttonLocation = 'cart_drawer';
      else if (target.closest('.popup, .modal, [data-popup]')) buttonLocation = 'popup';
      else if (target.closest('form')) buttonLocation = 'form';
      else if (target.closest('.product-card, [data-product-card]')) buttonLocation = 'product_card';
      else if (target.closest('.hero, [data-hero]')) buttonLocation = 'hero';

      sendEvent('button_click', {
        button_text: buttonText,
        button_href: (target.href || '').substring(0, 200),
        button_id: target.id || '',
        button_location: buttonLocation,
        element_type: target.tagName.toLowerCase()
      });

    }, true);
  }

  // ============================================
  // NAVIGATION TRACKING
  // ============================================

  function setupMenuTracking() {
    document.addEventListener('click', function(e) {
      var menuLink = e.target.closest('nav a, .nav a, .menu a, .header a, [data-menu-item]');
      if (menuLink) {
        sendEvent('menu_click', {
          menu_item: menuLink.textContent.trim().substring(0, 50),
          menu_href: menuLink.href || ''
        });
      }
    });
  }

  function setupSearchTracking() {
    document.addEventListener('submit', function(e) {
      var form = e.target;
      var searchInput = form.querySelector('input[type="search"], input[name="q"], input[name="query"]');
      if (searchInput) {
        sendEvent('search_query', { search_term: searchInput.value });
      }
    });

    if (getPageType() === 'search') {
      var params = new URLSearchParams(window.location.search);
      var query = params.get('q') || params.get('query') || '';
      var resultCount = document.querySelectorAll('.search-result, .product-card, [data-search-result]').length;

      sendEvent('search_results', {
        search_term: query,
        result_count: resultCount
      });
    }
  }

  function setupFilterTracking() {
    document.addEventListener('change', function(e) {
      var filter = e.target.closest('select[data-filter], input[data-filter], .filter select, .filter input');
      if (filter) {
        sendEvent('filter_applied', {
          filter_name: filter.name || filter.id || '',
          filter_value: filter.value
        });
      }
    });

    document.addEventListener('click', function(e) {
      var filterLink = e.target.closest('a[data-filter], .filter a, .facet a');
      if (filterLink) {
        sendEvent('filter_applied', { filter_value: filterLink.textContent.trim() });
      }
    });
  }

  function setupSortTracking() {
    document.addEventListener('change', function(e) {
      var sort = e.target.closest('select[name="sort_by"], select[data-sort], .sort-by select');
      if (sort) {
        sendEvent('sort_changed', { sort_value: sort.value });
      }
    });
  }

  function setupPaginationTracking() {
    document.addEventListener('click', function(e) {
      var pageLink = e.target.closest('.pagination a, [data-pagination] a, a[href*="page="]');
      if (pageLink) {
        sendEvent('pagination_click', { page_href: pageLink.href });
      }
    });
  }

  // ============================================
  // ENGAGEMENT TRACKING
  // ============================================

  function setupVideoTracking() {
    document.querySelectorAll('video').forEach(function(video) {
      video.addEventListener('play', function() {
        sendEvent('video_play', {
          video_src: video.src || video.currentSrc,
          video_duration: video.duration
        });
      });

      // Progress milestones — see WHERE viewers drop off, not just play vs.
      // finished. Skip decorative background clips (muted autoplay loops) so we
      // only measure videos people actually chose to watch.
      var isBackgroundVideo = video.loop || (video.muted && video.autoplay);
      if (!isBackgroundVideo) {
        var vidMilestones = [25, 50, 75];
        var vidReached = [];
        video.addEventListener('timeupdate', function() {
          if (!video.duration || !isFinite(video.duration)) return;
          var pct = Math.round((video.currentTime / video.duration) * 100);
          vidMilestones.forEach(function(m) {
            if (pct >= m && vidReached.indexOf(m) === -1) {
              vidReached.push(m);
              sendEvent('video_progress', {
                video_src: video.src || video.currentSrc,
                video_percent: m,
                video_duration: video.duration
              });
            }
          });
        });
      }

      // Unmute = real intent (they want the audio, not just the visuals).
      var wasMuted = video.muted;
      video.addEventListener('volumechange', function() {
        if (wasMuted && !video.muted) {
          sendEvent('video_unmute', { video_src: video.src || video.currentSrc });
        }
        wasMuted = video.muted;
      });

      video.addEventListener('ended', function() {
        sendEvent('video_complete', { video_src: video.src || video.currentSrc });
      });
      // Removed video_pause — too noisy, low value
    });
  }

  function setupPinchZoomTracking() {
    var lastPinchTime = 0;

    document.addEventListener('touchmove', function(e) {
      if (e.touches.length !== 2) return;

      var now = Date.now();
      if (now - lastPinchTime < 5000) return; // Throttled to once per 5s
      lastPinchTime = now;

      var t1 = e.touches[0];
      var t2 = e.touches[1];
      var midX = (t1.clientX + t2.clientX) / 2;
      var midY = (t1.clientY + t2.clientY) / 2;

      var target = document.elementFromPoint(midX, midY);
      if (!target) return;

      var img = target.closest('img') || target.querySelector('img');

      var productCard = target.closest('.product-card, [data-product-card], [data-product-id]');
      var productName = '';
      var productId = '';
      if (productCard) {
        productName = (productCard.querySelector('.product-card__title, .product-title, h3, h2') || {}).textContent || '';
        productId = productCard.getAttribute('data-product-id') || '';
      }
      if (!productName) {
        var info = getProductInfo();
        if (info.product_name) {
          productName = info.product_name;
          productId = info.product_id || '';
        }
      }

      sendEvent('pinch_zoom', {
        zoom_target: img ? img.alt || img.src.split('/').pop() : '',
        product_name: productName.trim(),
        product_id: productId,
        page_title: document.title
      });
    }, { passive: true });
  }

  function setupImageZoomTracking() {
    document.addEventListener('click', function(e) {
      var zoomTarget = e.target.closest('[data-zoom], .product-image, .gallery-image, img[data-enlargable]');
      if (zoomTarget) {
        sendEvent('image_zoom', {
          image_src: zoomTarget.src || zoomTarget.querySelector('img')?.src || ''
        });
      }
    });
  }

  function setupTabTracking() {
    document.addEventListener('click', function(e) {
      var tab = e.target.closest('[data-tab], .tab, [role="tab"]');
      if (tab) {
        sendEvent('tab_switch', { tab_name: tab.textContent.trim().substring(0, 50) });
      }
    });
  }

  function setupAccordionTracking() {
    document.addEventListener('click', function(e) {
      var accordion = e.target.closest('[data-accordion], .accordion-header, .collapsible-header, details summary, .lp-accordions__header, .lp-faq__question, .js-accordion-trigger');
      if (accordion) {
        var title = accordion.textContent.trim().substring(0, 100);

        /* Find the content panel next to the header */
        var contentEl = null;
        var parent = accordion.parentElement;
        if (parent) {
          contentEl = parent.querySelector('.lp-accordions__body, .lp-faq__answer, .c-product-accordion__content, [data-accordion-content], .accordion-content, .collapsible-content')
                   || accordion.nextElementSibling;
        }
        /* For <details> elements the content is inside the same element */
        if (!contentEl && accordion.tagName === 'SUMMARY') {
          contentEl = accordion.parentElement;
        }
        var contentPreview = '';
        if (contentEl) {
          contentPreview = contentEl.textContent.trim().replace(/\s+/g, ' ').substring(0, 200);
        }

        /* Identify which section the accordion is in */
        var section = accordion.closest('section, [data-section-type], [data-section-id]');
        var sectionName = '';
        if (section) {
          sectionName = section.id || section.dataset.sectionType || section.className.split(' ')[0] || '';
        }

        var data = {
          accordion_title: title,
          accordion_content: contentPreview,
          accordion_section: sectionName
        };

        /* If on a product page, include product info */
        if (getPageType() === 'product') {
          var info = getProductInfo();
          data.product_name = info.product_name || '';
          data.product_id = info.product_id || '';
        }

        sendEvent('accordion_open', data);
      }
    });
  }

  // Removed separate FAQ tracking — accordion tracking covers it

  // ============================================
  // FORM TRACKING (SIMPLIFIED)
  // Removed form_field_focus and form_field_blur — extremely noisy
  // ============================================

  function setupFormTracking() {
    var formStarted = {};

    document.addEventListener('focus', function(e) {
      var input = e.target;
      if (!input || !input.closest) return;
      var form = input.closest('form');
      if (!form) return;

      var formId = form.id || form.action || 'unknown_form';

      if (!formStarted[formId]) {
        formStarted[formId] = true;
        sendEvent('form_start', {
          form_id: formId,
          form_action: form.action || ''
        });
      }
      // Removed per-field focus/blur events
    }, true);

    document.addEventListener('submit', function(e) {
      var form = e.target;
      var formId = form.id || form.action || 'unknown_form';

      if (form.action && form.action.includes('/cart')) return;

      sendEvent('form_submit', {
        form_id: formId,
        form_action: form.action || ''
      });
    });

    document.addEventListener('invalid', function(e) {
      var input = e.target;
      if (!input || !input.closest) return;
      var form = input.closest('form');
      var formId = form ? (form.id || form.action || 'unknown_form') : 'unknown';

      sendEvent('form_error', {
        form_id: formId,
        field_name: input.name || input.id || '',
        error_message: input.validationMessage
      });
    }, true);
  }

  function setupNewsletterTracking() {
    document.addEventListener('submit', function(e) {
      var form = e.target;
      var emailInput = form.querySelector('input[type="email"]');
      var isNewsletter = form.classList.contains('newsletter') ||
                         form.id.includes('newsletter') ||
                         form.action.includes('newsletter') ||
                         form.action.includes('subscribe');

      if (emailInput && isNewsletter) {
        sendCriticalEvent('newsletter_signup', { form_id: form.id || form.action });
      }
    });
  }

  // ============================================
  // PRODUCT INTERACTION TRACKING
  // ============================================

  function setupVariantTracking() {
    document.addEventListener('change', function(e) {
      var select = e.target;

      if (select.name && (select.name.includes('option') || select.name.includes('variant'))) {
        sendEvent('variant_selected', {
          variant_type: select.name,
          variant_value: select.value,
          ...getProductInfo()
        });
        return; // Don't double-fire size/color below
      }
    });

    document.addEventListener('click', function(e) {
      var swatch = e.target.closest('[data-option-value], .swatch, .color-swatch, .size-swatch');
      if (swatch) {
        var value = swatch.dataset.optionValue || swatch.textContent.trim();
        sendEvent('variant_selected', {
          variant_value: value,
          ...getProductInfo()
        });
      }
    });
  }

  function setupQuantityTracking() {
    document.addEventListener('change', function(e) {
      var input = e.target;
      if (input.name === 'quantity' || input.classList.contains('quantity-input')) {
        sendEvent('quantity_changed', {
          quantity: input.value,
          ...getProductInfo()
        });
      }
    });
  }

  function setupReviewsTracking() {
    var reviewsSection = document.querySelector('#reviews, .reviews-section, [data-reviews-section]');
    if (reviewsSection) {
      var reviewsObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            sendEvent('reviews_viewed', getProductInfo());
            reviewsObserver.disconnect();
          }
        });
      }, { threshold: 0.5 });
      reviewsObserver.observe(reviewsSection);
    }
  }

  function setupWishlistTracking() {
    document.addEventListener('click', function(e) {
      var wishlistBtn = e.target.closest('[data-wishlist], .wishlist-btn, .add-to-wishlist, .favorite-btn');
      if (wishlistBtn) {
        sendEvent('wishlist_add', getProductInfo());
      }
    });
  }

  // ============================================
  // PROMO CODE TRACKING
  // ============================================

  function setupPromoCodeTracking() {
    document.addEventListener('submit', function(e) {
      var form = e.target;
      var promoInput = form.querySelector('input[name="discount"], input[name="promo"], input[name="coupon"]');
      if (promoInput && promoInput.value) {
        sendEvent('promo_code_applied', { promo_code: promoInput.value });
      }
    });
  }

  // ============================================
  // SOCIAL TRACKING
  // ============================================

  function setupSocialTracking() {
    document.addEventListener('click', function(e) {
      var socialLink = e.target.closest('a[href*="facebook.com/share"], a[href*="twitter.com/intent"], a[href*="pinterest.com/pin"], a[href*="linkedin.com/share"], [data-social-share]');
      if (socialLink) {
        var platform = 'unknown';
        var href = socialLink.href || '';
        if (href.includes('facebook')) platform = 'facebook';
        else if (href.includes('twitter')) platform = 'twitter';
        else if (href.includes('pinterest')) platform = 'pinterest';
        else if (href.includes('linkedin')) platform = 'linkedin';

        sendEvent('social_share', { platform: platform, shared_url: window.location.href });
      }

      var socialFollow = e.target.closest('a[href*="facebook.com/"], a[href*="instagram.com/"], a[href*="twitter.com/"], a[href*="tiktok.com/"], a[href*="youtube.com/"]');
      if (socialFollow && !socialFollow.href.includes('/share')) {
        var platform = 'unknown';
        var href = socialFollow.href || '';
        if (href.includes('facebook')) platform = 'facebook';
        else if (href.includes('instagram')) platform = 'instagram';
        else if (href.includes('twitter')) platform = 'twitter';
        else if (href.includes('tiktok')) platform = 'tiktok';
        else if (href.includes('youtube')) platform = 'youtube';

        sendEvent('social_click', { platform: platform });
      }
    });
  }

  // ============================================
  // ERROR TRACKING
  // ============================================

  function setupErrorTracking() {
    window.addEventListener('error', function(e) {
      // Skip cross-origin "Script error." noise — the browser masks the file,
      // line, and real message for third-party scripts, so it's not actionable.
      if (!e.message || e.message === 'Script error.' || (!e.filename && !e.lineno)) {
        return;
      }
      sendEvent('page_error', {
        error_message: e.message,
        error_source: e.filename,
        error_line: e.lineno
      });
    });
  }

  // ============================================
  // PERFORMANCE TRACKING
  // ============================================

  function trackPageLoadTime() {
    window.addEventListener('load', function() {
      requestAnimationFrame(function() {
        setTimeout(function() {
          var entry = performance.getEntriesByType('navigation')[0];
          if (!entry) return;

          var loadTime = Math.round(entry.loadEventEnd);
          var domReady = Math.round(entry.domContentLoadedEventEnd);

          if (loadTime <= 0) return;

          sendEvent('page_load_time', {
            load_time_ms: loadTime,
            dom_ready_ms: domReady
          });
        }, 0);
      });
    });
  }

  function setupRageClickTracking() {
    var clicks = [];
    var rageThreshold = 3;
    var rageWindow = 1000;

    document.addEventListener('click', function(e) {
      var now = Date.now();
      clicks.push({ time: now, x: e.clientX, y: e.clientY });

      clicks = clicks.filter(function(c) {
        return now - c.time < rageWindow;
      });

      if (clicks.length >= rageThreshold) {
        var firstClick = clicks[0];
        var allNearby = clicks.every(function(c) {
          return Math.abs(c.x - firstClick.x) < 50 && Math.abs(c.y - firstClick.y) < 50;
        });

        // Ignore text selection — triple-clicking a paragraph selects it and is
        // normal (copying a stat, a product name, etc.), NOT frustration. Without
        // this guard the metric counts people highlighting text as "rage".
        var hasSelection = window.getSelection &&
                           window.getSelection().toString().trim().length > 0;

        if (allNearby && !hasSelection) {
          // Flag whether the click actually hit something interactive. A real
          // rage-click is on a button/link that isn't responding; a click on
          // plain body text is far more likely benign.
          var interactiveEl = e.target.closest &&
                              e.target.closest('a, button, [role="button"], input, select, textarea, label, [onclick]');
          sendEvent('rage_click', {
            click_count: clicks.length,
            click_x: e.clientX,
            click_y: e.clientY,
            target_element: e.target.tagName + (e.target.id ? '#' + e.target.id : ''),
            target_interactive: interactiveEl ? 'yes' : 'no'
          });
          clicks = [];
        }
      }
    });
  }

  // ============================================
  // TEXT HIGHLIGHT TRACKING
  // Captures what visitors select/highlight — a strong signal of the exact
  // words they care about (stats, phrases, product names they copy to search).
  // ============================================

  function setupTextHighlightTracking() {
    var lastHighlight = '';
    var highlightTimer = null;

    function captureHighlight() {
      var sel = window.getSelection ? window.getSelection() : null;
      if (!sel) return;

      var text = sel.toString().trim().replace(/\s+/g, ' ');

      // Need a real selection of a few characters — ignore stray 1–2 char taps
      if (text.length < 3) return;
      // Don't re-fire for the exact same highlight
      if (text === lastHighlight) return;

      // Ignore selections inside form fields / editable areas (that's editing,
      // not reading) — those are already covered by input tracking.
      var anchorNode = sel.anchorNode;
      var anchorEl = anchorNode && (anchorNode.nodeType === 1 ? anchorNode : anchorNode.parentElement);
      if (anchorEl && anchorEl.closest &&
          anchorEl.closest('input, textarea, [contenteditable="true"], [contenteditable=""]')) {
        return;
      }

      lastHighlight = text;

      // Which part of the page was highlighted?
      var sectionName = '';
      if (anchorEl && anchorEl.closest) {
        var section = anchorEl.closest('section, article, [data-section-type], [data-section-id], .mbur-article__content, .rte');
        if (section) {
          sectionName = section.id ||
                        (section.dataset && section.dataset.sectionType) ||
                        (typeof section.className === 'string' ? section.className.split(' ')[0] : '') ||
                        '';
        }
      }

      var data = {
        highlighted_text: text.substring(0, 300),
        highlight_length: text.length,
        highlight_section: sectionName
      };

      // Attach page context so highlights are attributable
      if (getPageType() === 'product') {
        var info = getProductInfo();
        data.product_name = info.product_name || '';
        data.product_id = info.product_id || '';
      } else if (getPageType() === 'blog') {
        var h1 = document.querySelector('h1, .article-title, .blog-title');
        if (h1) data.article_title = h1.textContent.trim().substring(0, 150);
      }

      sendEvent('text_highlight', data);
    }

    function scheduleCapture() {
      if (highlightTimer) clearTimeout(highlightTimer);
      // Small delay so the selection is final — especially on mobile where the
      // user drags the selection handles after the initial touchend.
      highlightTimer = setTimeout(captureHighlight, 600);
    }

    document.addEventListener('mouseup', scheduleCapture);
    document.addEventListener('touchend', scheduleCapture);

    // When the selection is cleared, reset the dedupe key so highlighting the
    // same passage again later counts as a fresh highlight.
    document.addEventListener('selectionchange', function() {
      var sel = window.getSelection ? window.getSelection() : null;
      if (!sel || sel.toString().trim().length === 0) {
        lastHighlight = '';
      }
    });
  }

  // ============================================
  // STICKY ADD-TO-CART TRACKING
  // The floating sticky bar's Add button (landing-hero) — distinct from the
  // main product-form button, so you can see which placement drives adds.
  // ============================================

  function setupStickyAtcTracking() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.sticky-add-btn, #stickyAddBtn, .sticky-cart button, .sticky-cart [type="submit"]');
      if (!btn) return;

      var info = getProductInfo();
      var variantSel = document.querySelector('#stickyVariantSelect, .sticky-cart-variant');
      var variantLabel = '';
      if (variantSel && variantSel.options && variantSel.selectedIndex >= 0) {
        variantLabel = variantSel.options[variantSel.selectedIndex].text;
      }

      sendEvent('sticky_add_to_cart_click', {
        product_name: info.product_name || '',
        product_id: info.product_id || '',
        variant_value: variantSel ? variantSel.value : '',
        variant_label: variantLabel
      });
    }, true);
  }

  // ============================================
  // DEAD CLICK TRACKING
  // A click on something that LOOKS clickable (pointer cursor) but isn't
  // actually interactive and does nothing — a "false affordance". This is the
  // single-click cousin of rage_click and pinpoints "looks like a link but
  // isn't" spots (e.g. product names in blog body text).
  // ============================================

  function setupDeadClickTracking() {
    var lastDead = 0;

    document.addEventListener('click', function(e) {
      var now = Date.now();
      if (now - lastDead < 500) return; // throttle

      var el = e.target;
      if (!el || !el.closest) return;

      // If the click is on/inside anything genuinely interactive, it's not dead.
      if (el.closest('a, button, [role="button"], input, select, textarea, label, summary, details, [onclick], [data-zoom], [data-tab], .swatch')) {
        return;
      }

      // Only count elements that LOOK clickable — pointer cursor = false affordance.
      var cursor = '';
      try { cursor = window.getComputedStyle(el).cursor; } catch (err) {}
      if (cursor !== 'pointer') return;

      // Don't confuse text selection with a dead click.
      if (window.getSelection && window.getSelection().toString().trim().length > 0) return;

      lastDead = now;
      sendEvent('dead_click', {
        click_x: e.clientX,
        click_y: e.clientY,
        target_element: el.tagName + (el.id ? '#' + el.id : ''),
        target_class: (typeof el.className === 'string' ? el.className.split(' ')[0] : ''),
        target_text: (el.innerText || el.textContent || '').trim().replace(/\s+/g, ' ').substring(0, 60)
      });
    }, true);
  }

  // ============================================
  // COPY-TO-CLIPBOARD TRACKING
  // What visitors actually copy (product names, discount codes, address) — a
  // stronger intent signal than a highlight alone.
  // ============================================

  function setupCopyTracking() {
    document.addEventListener('copy', function() {
      var text = '';
      if (window.getSelection) {
        text = window.getSelection().toString().trim().replace(/\s+/g, ' ');
      }
      if (!text || text.length < 2) return;

      var data = {
        copied_text: text.substring(0, 300),
        copied_length: text.length
      };
      if (getPageType() === 'product') {
        var info = getProductInfo();
        data.product_name = info.product_name || '';
        data.product_id = info.product_id || '';
      } else if (getPageType() === 'blog') {
        var h1 = document.querySelector('h1, .article-title, .blog-title');
        if (h1) data.article_title = h1.textContent.trim().substring(0, 150);
      }

      sendEvent('copy_to_clipboard', data);
    });
  }

  // ============================================
  // OUTBOUND CLICK TRACKING
  // Any click to an off-site link (esp. "SHOP ON ETSY") — quantify the traffic
  // you're sending away, and to where.
  // ============================================

  function setupOutboundClickTracking() {
    document.addEventListener('click', function(e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.href || '';
      if (!/^https?:\/\//i.test(href)) return; // skip mailto:, tel:, relative

      var host;
      try { host = new URL(href).hostname.toLowerCase(); } catch (err) { return; }

      // Same-site (storefront or myshopify) is not outbound
      if (host === window.location.hostname.toLowerCase() ||
          host.indexOf('mburcandle.co') > -1 ||
          host.indexOf('myshopify.com') > -1) return;

      var platform = 'other';
      if (host.indexOf('etsy') > -1) platform = 'etsy';
      else if (host.indexOf('instagram') > -1) platform = 'instagram';
      else if (host.indexOf('facebook') > -1) platform = 'facebook';
      else if (host.indexOf('tiktok') > -1) platform = 'tiktok';
      else if (host.indexOf('youtube') > -1) platform = 'youtube';
      else if (host.indexOf('pinterest') > -1) platform = 'pinterest';

      sendEvent('outbound_click', {
        outbound_url: href.substring(0, 300),
        outbound_domain: host,
        outbound_platform: platform,
        link_text: (link.innerText || link.textContent || '').trim().replace(/\s+/g, ' ').substring(0, 80)
      });
    }, true);
  }

  // ============================================
  // PRODUCT IMPRESSION TRACKING
  // A product card scrolls into view — see which products get SEEN vs clicked
  // (find weak thumbnails / bad positions). Once per product per page.
  // ============================================

  function setupProductImpressionTracking() {
    if (!('IntersectionObserver' in window)) return;

    var cards = document.querySelectorAll('.product-card, [data-product-card], .cp-card, .related-card, [data-product-id]');
    if (!cards.length) return;

    var seen = {};
    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var card = entry.target;

        var linkEl = card.querySelector('a[href*="/products/"]') ||
                     (card.closest ? card.closest('a[href*="/products/"]') : null);
        var url = linkEl ? linkEl.getAttribute('href') : (card.getAttribute('data-product-url') || '');
        var handle = url ? ((url.split('/products/')[1] || '').split('?')[0].split('/')[0]) : '';
        var key = handle || url || card.getAttribute('data-product-id') || '';

        if (!key || seen[key]) { io.unobserve(card); return; }
        seen[key] = true;

        // nameFromCard covers every card variant on the site and falls back to
        // aria-label / img alt / the handle — the old selector list missed the
        // cross-sell, cart-drawer-rec and search card markup, which is why 38%
        // of impressions had no product_name.
        var impression = {
          product_name: nameFromCard(card) || humanizeHandle(handle),
          product_handle: handle,
          product_url: url,
          collection_name: (window.location.pathname.split('/collections/')[1] || '').split('/')[0]
        };
        // Image-rotation experiment (blog inline cards): the card carries which
        // photo variant was shown. Log it so image-level view counts land in
        // the sheet — pair with inline_product_click to get click-rate/image.
        if (card.getAttribute('data-card-image-index') !== null) {
          impression.card_image_index = card.getAttribute('data-card-image-index');
          impression.card_image_id = card.getAttribute('data-card-image-id') || '';
          impression.card_context = card.getAttribute('data-card-context') || '';
        }
        sendEvent('product_impression', impression);
        io.unobserve(card);
      });
    }, { threshold: 0.5 });

    cards.forEach(function(c) { io.observe(c); });
  }

  // ============================================
  // INLINE PRODUCT CARD CLICK (blog articles)
  // Click-through on an in-article product card. Carries the image-rotation
  // variant (card_image_index/id) when the experiment is on, so the sheet can
  // compute click-rate PER PHOTO: clicks ÷ product_impression with the same
  // card_image_index.
  // ============================================

  function setupInlineProductClickTracking() {
    document.addEventListener('click', function(e) {
      var link = e.target.closest && e.target.closest('.product-card__link, .product-card a[href*="/products/"]');
      if (!link) return;
      var card = link.closest('.product-card');
      if (!card) return;
      // Only article-embedded cards (inline shortcode or article carousel)
      var ctx = card.getAttribute('data-card-context') || '';
      if (ctx !== 'inline' && ctx !== 'carousel') return;

      var handle = card.getAttribute('data-product-handle') ||
                   ((link.getAttribute('href') || '').split('/products/')[1] || '').split('?')[0];
      var h1 = document.querySelector('h1, .article-title, .blog-title');

      var data = {
        product_handle: handle,
        card_context: ctx,
        article_title: h1 ? h1.textContent.trim().substring(0, 150) : document.title
      };
      if (card.getAttribute('data-card-image-index') !== null) {
        data.card_image_index = card.getAttribute('data-card-image-index');
        data.card_image_id = card.getAttribute('data-card-image-id') || '';
      }
      // critical: the click navigates away — a queued batch would be lost
      sendCriticalEvent('inline_product_click', data);
    }, true);
  }

  // ============================================
  // QUICK ADD TRACKING
  // Add straight from a card (cart-drawer recs / related products) — which
  // surface drives adds without visiting the PDP.
  // ============================================

  function setupQuickAddTracking() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.js-quick-add, .related-card__quick-add, [data-quick-add]');
      if (!btn) return;

      var card = btn.closest('.product-card, .cart-drawer__rec, .related-card, [data-product-card]');
      var nameEl = card && card.querySelector('.product-card__title, .related-card__title, .cart-drawer__rec-title, .product-title, h3, h2');

      var location = 'collection';
      if (btn.closest('.cart-drawer, [data-cart-drawer]')) location = 'cart_drawer_recs';
      else if (btn.closest('.related-card, [data-related], .enhanced-related-products')) location = 'related_products';

      sendEvent('quick_add_click', {
        variant_id: btn.getAttribute('data-variant-id') || '',
        product_name: nameEl ? nameEl.textContent.trim().substring(0, 120) : '',
        quick_add_location: location
      });
    }, true);
  }

  // ============================================
  // SHIPPING ESTIMATE RESULT TRACKING
  // After the ZIP is entered, capture the rates shown (or the error) — completes
  // the shipping-estimator story started by shipping_zip_entered.
  // ============================================

  function setupShippingEstimateTracking() {
    var lastKey = '';

    function readResults(container) {
      var errorEl = container.querySelector('.shipping-calc__error');
      var options = container.querySelectorAll('.shipping-calc__option');

      if (errorEl && errorEl.textContent.trim()) {
        var msg = errorEl.textContent.trim().substring(0, 150);
        if (msg === lastKey) return;
        lastKey = msg;
        sendEvent('shipping_estimate_result', {
          estimate_status: 'error',
          estimate_message: msg,
          rate_count: 0
        });
      } else if (options.length) {
        var rates = [];
        options.forEach(function(o) {
          var n = o.querySelector('.shipping-calc__option-name');
          var p = o.querySelector('.shipping-calc__option-price');
          rates.push(((n ? n.textContent.trim() : '') + ' ' + (p ? p.textContent.trim() : '')).trim());
        });
        var joined = rates.join(' | ').substring(0, 250);
        if (joined === lastKey) return;
        lastKey = joined;
        sendEvent('shipping_estimate_result', {
          estimate_status: 'rates',
          rate_count: options.length,
          rates: joined
        });
      }
    }

    function attach(container) {
      if (!container || container._mburWatched) return;
      container._mburWatched = true;
      var debounce = null;
      var obs = new MutationObserver(function() {
        if (debounce) clearTimeout(debounce);
        debounce = setTimeout(function() { readResults(container); }, 350);
      });
      obs.observe(container, { childList: true, subtree: true });
    }

    var existing = document.querySelector('.shipping-calc__results');
    if (existing) attach(existing);

    // The cart drawer can be re-rendered, so watch for the results box
    // re-appearing — but only within the DRAWER subtree. This observer used to
    // watch the whole document.body, firing on every DOM mutation anywhere on
    // every page; the shipping calc only ever lives inside the cart drawer.
    var drawerRoot = document.querySelector('.js-cart-drawer') || document.querySelector('.cart-drawer');
    if (drawerRoot) {
      var docObs = new MutationObserver(function() {
        var r = drawerRoot.querySelector('.shipping-calc__results');
        if (r) attach(r);
      });
      docObs.observe(drawerRoot, { childList: true, subtree: true });
    }
  }

  // ============================================
  // READ COMPLETE TRACKING (blog)
  // Reached the end of an article WITH real dwell time = genuinely read, not
  // bounced. Content ROI signal.
  // ============================================

  function setupReadCompleteTracking() {
    if (getPageType() !== 'blog') return;

    var start = Date.now();
    var fired = false;
    var lastCheck = 0;

    function onScroll() {
      var now = Date.now();
      if (now - lastCheck < 500) return;
      lastCheck = now;
      if (fired) return;

      var scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;

      var pct = (window.scrollY / scrollHeight) * 100;
      var elapsed = (now - start) / 1000;

      // Bottom of the article AND enough time to have actually read it
      if (pct >= 90 && elapsed >= 20) {
        fired = true;
        var h1 = document.querySelector('h1, .article-title, .blog-title');
        sendEvent('read_complete', {
          article_title: h1 ? h1.textContent.trim().substring(0, 150) : document.title,
          time_to_complete_seconds: Math.round(elapsed)
        });
        window.removeEventListener('scroll', onScroll);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ============================================
  // WEB VITALS TRACKING
  // Real Core Web Vitals (LCP / CLS / INP) — catches slow real-user experiences
  // that page_load_time alone misses (you had a 51-second load in the data).
  // ============================================

  function setupWebVitalsTracking() {
    if (!('PerformanceObserver' in window)) return;

    var lcp = 0, cls = 0, inp = 0;

    try {
      new PerformanceObserver(function(list) {
        var entries = list.getEntries();
        var last = entries[entries.length - 1];
        if (last) lcp = Math.round(last.startTime);
      }).observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {}

    try {
      new PerformanceObserver(function(list) {
        list.getEntries().forEach(function(entry) {
          if (!entry.hadRecentInput) cls += entry.value;
        });
      }).observe({ type: 'layout-shift', buffered: true });
    } catch (e) {}

    try {
      new PerformanceObserver(function(list) {
        list.getEntries().forEach(function(entry) {
          var d = entry.duration || 0;
          if (d > inp) inp = Math.round(d);
        });
      }).observe({ type: 'event', buffered: true, durationThreshold: 40 });
    } catch (e) {}

    var reported = false;
    function report() {
      if (reported) return;
      reported = true;
      var rating = (lcp > 4000 || cls > 0.25 || inp > 500) ? 'poor'
                 : (lcp > 2500 || cls > 0.1 || inp > 200) ? 'needs_improvement'
                 : 'good';
      sendEvent('web_vitals', {
        lcp_ms: lcp,
        cls: Math.round(cls * 1000) / 1000,
        inp_ms: inp,
        vitals_rating: rating
      });
    }

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') report();
    }, { capture: true });
    window.addEventListener('pagehide', report, { capture: true });
  }

  // ============================================
  // TAB SWITCH TRACKING
  // Visitor leaves the tab and (maybe) returns — explains inflated time-on-page
  // (multitasking vs. actually engaged).
  // ============================================

  function setupTabSwitchTracking() {
    var awayCount = 0;
    var hiddenAt = 0;

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') {
        awayCount++;
        hiddenAt = Date.now();
        sendEvent('tab_switch_away', { away_count: awayCount });
      } else if (document.visibilityState === 'visible' && hiddenAt) {
        var awaySeconds = Math.round((Date.now() - hiddenAt) / 1000);
        hiddenAt = 0;
        sendEvent('tab_switch_return', {
          away_seconds: awaySeconds,
          away_count: awayCount
        });
      }
    });
  }

  // ============================================
  // SCROLL SKIM TRACKING
  // Very fast scrolling = skimming / flinging past content rather than reading.
  // A content-quality signal.
  // ============================================

  function setupScrollSkimTracking() {
    var lastY = window.scrollY;
    var lastT = Date.now();
    var lastSkim = 0;

    window.addEventListener('scroll', function() {
      var now = Date.now();
      var dt = now - lastT;
      if (dt < 50) return;

      var dy = Math.abs(window.scrollY - lastY);
      var velocity = dy / dt; // px per ms
      lastY = window.scrollY;
      lastT = now;

      // > ~3 px/ms (3000 px/s) sustained = flinging past content, not reading.
      if (velocity > 3 && now - lastSkim > 3000) {
        lastSkim = now;
        sendEvent('scroll_skim', {
          scroll_velocity_px_per_s: Math.round(velocity * 1000),
          scroll_y: Math.round(window.scrollY)
        });
      }
    }, { passive: true });
  }

  // ============================================
  // NEW TAB OPEN TRACKING
  // Opening a link in a new tab (cmd/ctrl-click, middle-click, or target=_blank)
  // = comparison shopping — the visitor is lining products up side by side.
  // ============================================

  function setupNewTabTracking() {
    function fire(link, method) {
      var href = link.href || '';
      if (!href || href.indexOf('javascript:') === 0) return;
      sendEvent('new_tab_open', {
        new_tab_url: href.substring(0, 300),
        new_tab_method: method,
        link_text: (link.innerText || link.textContent || '').trim().replace(/\s+/g, ' ').substring(0, 80),
        is_product: href.indexOf('/products/') > -1 ? 'yes' : 'no'
      });
    }

    // Cmd/Ctrl-click opens a new tab; a plain click on a target="_blank" link does too.
    document.addEventListener('click', function(e) {
      var link = e.target.closest('a[href]');
      if (!link) return;
      if (e.metaKey || e.ctrlKey) {
        fire(link, 'modifier_click');
      } else if ((link.target || '').toLowerCase() === '_blank') {
        fire(link, 'target_blank');
      }
    }, true);

    // Middle-click (mouse button 1) opens a new tab — surfaced via auxclick.
    document.addEventListener('auxclick', function(e) {
      if (e.button !== 1) return;
      var link = e.target.closest('a[href]');
      if (link) fire(link, 'middle_click');
    }, true);
  }

  // ============================================
  // REFERRER SOURCE TRACKING
  // Once per session — resolves where the visitor actually came from into a
  // clean source (Google, Reddit, ChatGPT, Etsy, email…) for when there's no
  // UTM. Fills the attribution gap.
  // ============================================

  function setupReferrerSourceTracking() {
    // Only the FIRST page of the session reflects the true external entry point.
    if (sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'referrer_logged') === 'true') return;
    sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'referrer_logged', 'true');

    var ref = document.referrer || '';
    var host = '';
    try { if (ref) host = new URL(ref).hostname.toLowerCase(); } catch (e) {}

    // A campaign UTM is the most authoritative source when present.
    if (_utm.utm_source) {
      sendEvent('referrer_source', {
        referrer_source: _utm.utm_source,
        referrer_medium: _utm.utm_medium || 'campaign',
        referrer_domain: host,
        referrer_url: ref.substring(0, 300)
      });
      return;
    }

    var source = 'direct';
    var medium = 'none';

    if (host) {
      var sameSite = host === window.location.hostname.toLowerCase() ||
                     host.indexOf('mburcandle.co') > -1 ||
                     host.indexOf('myshopify.com') > -1;
      if (sameSite) {
        source = 'internal';
        medium = 'internal';
      } else {
        var map = [
          { m: 'search',      s: 'google',     k: ['google.'] },
          { m: 'search',      s: 'bing',       k: ['bing.'] },
          { m: 'search',      s: 'duckduckgo', k: ['duckduckgo.'] },
          { m: 'search',      s: 'yahoo',      k: ['search.yahoo'] },
          { m: 'search',      s: 'ecosia',     k: ['ecosia.'] },
          { m: 'ai',          s: 'chatgpt',    k: ['chatgpt.com', 'chat.openai.com', 'openai.'] },
          { m: 'ai',          s: 'perplexity', k: ['perplexity.'] },
          { m: 'ai',          s: 'gemini',     k: ['gemini.google', 'bard.google'] },
          { m: 'ai',          s: 'claude',     k: ['claude.ai'] },
          { m: 'ai',          s: 'copilot',    k: ['copilot.microsoft'] },
          { m: 'social',      s: 'reddit',     k: ['reddit.'] },
          { m: 'social',      s: 'facebook',   k: ['facebook.', 'fb.com', 'fb.me', 'l.facebook'] },
          { m: 'social',      s: 'instagram',  k: ['instagram.', 'l.instagram'] },
          { m: 'social',      s: 'tiktok',     k: ['tiktok.'] },
          { m: 'social',      s: 'pinterest',  k: ['pinterest.', 'pin.it'] },
          { m: 'social',      s: 'twitter',    k: ['twitter.', 'x.com', 't.co'] },
          { m: 'social',      s: 'youtube',    k: ['youtube.', 'youtu.be'] },
          { m: 'social',      s: 'linkedin',   k: ['linkedin.', 'lnkd.in'] },
          { m: 'marketplace', s: 'etsy',       k: ['etsy.'] },
          { m: 'email',       s: 'email',      k: ['mail.google', 'outlook.', 'mail.yahoo'] }
        ];
        var matched = false;
        for (var i = 0; i < map.length && !matched; i++) {
          for (var j = 0; j < map[i].k.length; j++) {
            if (host.indexOf(map[i].k[j]) > -1) {
              source = map[i].s;
              medium = map[i].m;
              matched = true;
              break;
            }
          }
        }
        if (!matched) {
          source = host.replace(/^www\./, '');
          medium = 'referral';
        }
      }
    }

    sendEvent('referrer_source', {
      referrer_source: source,
      referrer_medium: medium,
      referrer_domain: host,
      referrer_url: ref.substring(0, 300)
    });
  }

  // ============================================
  // QUIZ TRACKING
  // ============================================

  function setupQuizTracking() {
    // Only pages that actually render a quiz get the listener + the body-wide
    // MutationObserver. On every other page (all blog/product/collection views)
    // the observer fired on every DOM mutation for nothing.
    var hasQuiz = document.querySelector(
      '[data-quiz-start], .quiz-start, .start-quiz, [data-quiz-question], .quiz-question, [data-quiz-answer], .quiz-option'
    );
    if (!hasQuiz) return;

    document.addEventListener('click', function(e) {
      var quizStart = e.target.closest('[data-quiz-start], .quiz-start, .start-quiz');
      if (quizStart) {
        sendEvent('quiz_started');
      }

      var quizAnswer = e.target.closest('[data-quiz-answer], .quiz-answer, .quiz-option');
      if (quizAnswer) {
        var questionEl = quizAnswer.closest('[data-quiz-question], .quiz-question');
        sendEvent('quiz_question_answered', {
          question: questionEl ? questionEl.dataset.quizQuestion || '' : '',
          answer: quizAnswer.textContent.trim().substring(0, 100)
        });
      }
    });

    var quizResultsObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 &&
              (node.classList.contains('quiz-results') ||
               node.classList.contains('quiz-complete') ||
               node.querySelector && node.querySelector('.quiz-results'))) {
            sendEvent('quiz_completed');
          }
        });
      });
    });

    quizResultsObserver.observe(document.body, { childList: true, subtree: true });
  }

  // ============================================
  // POPUP TRACKING
  // ============================================

  function setupPopupTracking() {
    var popupObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            var isPopup = node.classList.contains('popup') ||
                         node.classList.contains('modal') ||
                         node.classList.contains('overlay') ||
                         (typeof node.id === 'string' && node.id.includes('popup')) ||
                         (typeof  node.id === 'string' && node.id.includes('modal'));

            if (isPopup) {
              sendEvent('popup_shown', {
                popup_id: node.id || node.className.substring(0, 50)
              });

              node.addEventListener('click', function(e) {
                var closeBtn = e.target.closest('.close, .popup-close, .modal-close, [data-close]');
                if (closeBtn) {
                  sendEvent('popup_closed', {
                    popup_id: node.id || node.className.substring(0, 50)
                  });
                }
              });
            }
          }
        });
      });
    });

    popupObserver.observe(document.body, { childList: true, subtree: true });
  }

  function setupExitIntentTracking() {
    var exitIntentFired = false;

    document.addEventListener('mouseout', function(e) {
      if (e.clientY < 10 && !exitIntentFired) {
        exitIntentFired = true;
        sendEvent('exit_intent_triggered');
      }
    });
  }

  // ============================================
  // MOBILE TRACKING (SIMPLIFIED)
  // ============================================

  function setupMobileTracking() {
    if (getDeviceType() !== 'Mobile' && getDeviceType() !== 'Tablet') return;

    var touchStart = null;
    var lastSwipeTime = 0;

    document.addEventListener('touchstart', function(e) {
      touchStart = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now()
      };
    }, { passive: true });

    document.addEventListener('touchend', function(e) {
      if (!touchStart) return;

      var now = Date.now();
      if (now - lastSwipeTime < 2000) return; // Throttle swipes
      
      var touchEnd = {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY
      };

      var diffX = touchEnd.x - touchStart.x;
      var diffY = touchEnd.y - touchStart.y;
      var timeDiff = now - touchStart.time;

      if (timeDiff > 500) return;

      if (Math.abs(diffX) > 100 && Math.abs(diffY) < 50) {
        lastSwipeTime = now;
        /* If a product gallery is on the page, label the swipe with the image
           currently being viewed so the sheet shows WHICH image, not just a direction. */
        var activeImg = document.querySelector('.pp-gallery__image.is-active, .product-gallery__image.is-active');
        var swipeData = {};
        if (activeImg) {
          swipeData.image_label = activeImg.getAttribute('data-image-label') || activeImg.alt || '';
        }
        sendEvent(diffX > 0 ? 'swipe_right' : 'swipe_left', swipeData);
      }
    }, { passive: true });

    // Removed orientation_change and duplicate pinch_zoom — low value
  }

  // ============================================
  // SCROLL DEPTH
  // ============================================

  function setupScrollTracking() {
    const milestones = [25, 50, 75, 90];
    const tracked = [];
    var scrollThrottle = 0;
    var maxScrollDepth = 0;

    window.addEventListener('scroll', function() {
      var now = Date.now();
      if (now - scrollThrottle < 500) return; // Throttle scroll handler
      scrollThrottle = now;

      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;

      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

      // Track max scroll depth for session_end
      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;
        sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'max_scroll_depth', maxScrollDepth.toString());
      }

      milestones.forEach(function(milestone) {
        if (scrollPercent >= milestone && tracked.indexOf(milestone) === -1) {
          tracked.push(milestone);
          sendEvent('scroll_depth', { scroll_depth: milestone + '%' });
        }
      });
    }, { passive: true });
  }

  // ============================================
  // TIME ON PAGE
  // ============================================

  function setupTimeTracking() {
    const milestones = [30, 60, 120, 300];
    const tracked = [];
    const startTime = Date.now();

    var timeTimer = setInterval(function() {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);

      milestones.forEach(function(milestone) {
        if (elapsed >= milestone && tracked.indexOf(milestone) === -1) {
          tracked.push(milestone);
          sendEvent('time_on_page', { time_on_page: milestone });
        }
      });

      // Nothing left to report after the last milestone — stop waking the CPU
      // every 5s for the rest of the page's life.
      if (tracked.length === milestones.length) {
        clearInterval(timeTimer);
      }
    }, 5000);
  }

  // ============================================
  // SESSION END
  // ============================================

  function setupEndSessionTracking() {
    var sessionStartTime = parseInt(sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'session_start_time')) || Date.now();

    // Track whether the current navigation is internal (same-site)
    var isInternalNavigation = false;

    // Detect clicks on internal links to flag same-site navigations
    document.addEventListener('click', function(e) {
      var link = e.target.closest('a');
      if (link && link.href) {
        try {
          var linkUrl = new URL(link.href, window.location.origin);
          if (linkUrl.origin === window.location.origin) {
            isInternalNavigation = true;
          }
        } catch(err) {}
      }
    }, true);

    // Also detect form submissions to same origin
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (form && form.action) {
        try {
          var formUrl = new URL(form.action, window.location.origin);
          if (formUrl.origin === window.location.origin) {
            isInternalNavigation = true;
          }
        } catch(err) {}
      }
    }, true);

    function sendSessionEnd() {
      // Prevent duplicate sends — use sessionStorage so it persists across page loads
      if (sessionEnded) return;
      if (sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'session_ended') === 'true') return;

      // Skip session_end for internal navigations (same-site page transitions)
      if (isInternalNavigation) {
        isInternalNavigation = false; // Reset for next navigation
        flushQueue();
        return;
      }

      sessionEnded = true;
      sessionStorage.setItem(CONFIG.STORAGE_PREFIX + 'session_ended', 'true');

      // Flush any remaining batched events first
      flushQueue();

      var sessionDuration = Math.floor((Date.now() - sessionStartTime) / 1000);
      var pageCount = sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'page_count') || 1;

      // Get additional engagement metrics
      var scrollDepth = parseInt(sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'max_scroll_depth') || '0');

      var endEvents = [{
        timestamp: new Date().toISOString(),
        event_seq: nextEventSeq(),
        event_ms: Date.now(),
        event_type: 'session_end',
        visitor_id: _visitorId,
        session_id: _sessionId,
        client_ip: _clientIP,
        fingerprint: _fingerprint,
        session_duration_seconds: sessionDuration,
        pages_viewed: parseInt(pageCount),
        exit_page: window.location.href,
        max_scroll_depth: scrollDepth + '%',
        total_time_seconds: sessionDuration
      }];

      // Cart abandonment — items still in the cart, but the visitor left without
      // starting checkout or purchasing. Rides along on the same unload beacon.
      var lastCartCount = parseInt(sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'last_cart_count') || '0');
      var checkoutStarted = sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'checkout_tracked') === 'true';
      var purchased = sessionStorage.getItem(CONFIG.STORAGE_PREFIX + 'purchase_tracked') === 'true';
      if (lastCartCount > 0 && !checkoutStarted && !purchased) {
        endEvents.push({
          timestamp: new Date().toISOString(),
          event_seq: nextEventSeq(),
          event_ms: Date.now(),
          event_type: 'cart_abandon_intent',
          visitor_id: _visitorId,
          session_id: _sessionId,
          client_ip: _clientIP,
          fingerprint: _fingerprint,
          cart_item_count: lastCartCount,
          exit_page: window.location.href,
          session_duration_seconds: sessionDuration,
          pages_viewed: parseInt(pageCount)
        });
      }

      // session_end (and cart_abandon_intent) are built inline here rather than
      // via sendEvent/sendCriticalEvent, so they need the flags stamped too.
      // Applied after the cart_abandon_intent push so EVERY event in the array
      // is covered — the server reads these per-event, not per-envelope.
      for (var ei = 0; ei < endEvents.length; ei++) {
        Object.assign(endEvents[ei], mburBotFlags());
      }

      var payload = JSON.stringify({ events: endEvents });

      // Use sendBeacon for guaranteed delivery even during unload
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CONFIG.WEBHOOK_URL, payload);
      }
    }

    // Only use pagehide — most reliable, fires once
    window.addEventListener('pagehide', sendSessionEnd, { capture: true });
  }

  // ============================================
  // PUBLIC API
  // ============================================

  window.MburTracker = {
    trackSampleSelection: function(scentName) {
      sendEvent('sample_selection', { scent_name: scentName });
    },

    trackSampleRequest: function(scentsArray) {
      const scents = Array.isArray(scentsArray) ? scentsArray.join(', ') : scentsArray;
      sendCriticalEvent('sample_request', { scents_selected: scents });
    },

    trackEmail: function(email) {
      sendCriticalEvent('email_captured', { email: email });
    },

    trackCustomEvent: function(eventType, data) {
      sendEvent(eventType, data);
    },

    getVisitorId: function() {
      return _visitorId;
    },

    getSessionId: function() {
      return _sessionId;
    }
  };

  // ============================================
  // INITIALIZE
  // ============================================

  function init() {
    log('Initializing batched tracker');

    // Start the batch timer
    startBatchTimer();

    trackSessionStart();

    setTimeout(function() {
      trackPageView();
      trackProductView();
      trackCollectionView();
      trackBlogRead();
      trackCartView();
      trackCheckoutInitiated();
      trackPurchaseComplete();
      track404Page();

      setupAddToCartTracking();
      setupCartRemoveTracking();
      setupOrderNotesTracking();
      if (getPageType() === 'cart') trackCartContents();

      setupButtonClickTracking();
      setupTextInputTracking();
      setupMenuTracking();
      setupSearchTracking();
      setupFilterTracking();
      setupSortTracking();
      setupPaginationTracking();

      setupVideoTracking();
      setupImageZoomTracking();
      setupPinchZoomTracking();
      setupTabTracking();
      setupAccordionTracking();

      setupFormTracking();
      setupNewsletterTracking();

      setupVariantTracking();
      setupQuantityTracking();
      setupReviewsTracking();
      setupWishlistTracking();
      setupPromoCodeTracking();

      setupSocialTracking();

      setupErrorTracking();
      trackPageLoadTime();
      setupRageClickTracking();
      setupTextHighlightTracking();
      setupStickyAtcTracking();
      setupDeadClickTracking();
      setupCopyTracking();
      setupOutboundClickTracking();
      setupProductImpressionTracking();
      setupInlineProductClickTracking();
      setupQuickAddTracking();
      setupShippingEstimateTracking();
      setupReadCompleteTracking();
      setupWebVitalsTracking();
      setupTabSwitchTracking();
      setupScrollSkimTracking();
      setupNewTabTracking();
      setupReferrerSourceTracking();

      setupQuizTracking();
      setupPopupTracking();
      setupExitIntentTracking();

      setupMobileTracking();

      setupScrollTracking();
      setupTimeTracking();

      setupEndSessionTracking();

      log('Batched tracker initialized');
    }, 100);
  }

  function boot() {
    // Start the IP lookup in the background but DON'T gate init() on it — the
    // old `fetchClientIP().then(init)` stalled all tracking up to 2s behind
    // api.ipify.org on every page view. IP-based blocklist filtering still
    // holds: flushQueue() and sendCriticalEvent() wait on _ipPromise and
    // re-check isBlockedIP() before anything is sent.
    _ipPromise = fetchClientIP();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();